import { useTranslation } from "react-i18next";
import { Spinner } from "@/shared/ui";
import { type ComposerAttachment, fileExtensionLabel } from "../lib/attachments";
import { CloseIcon } from "./icons";

interface ComposerAttachmentsProps {
  attachments: ComposerAttachment[];
  onRemove: (localId: string) => void;
}

/** Pending attachments shown as square cards: an image thumbnail, or the file
 *  name with a type badge. Uploading state overlays a spinner. */
export function ComposerAttachments({ attachments, onRemove }: ComposerAttachmentsProps) {
  const { t } = useTranslation();
  if (attachments.length === 0) return null;

  return (
    <ul className="mb-3 flex flex-wrap gap-2.5">
      {attachments.map((a) => {
        const ext = fileExtensionLabel(a.fileName);
        return (
          <li
            key={a.localId}
            className="group relative size-[116px] shrink-0 overflow-hidden rounded-xl bg-canvas-soft"
          >
            {a.previewUrl ? (
              <>
                <img src={a.previewUrl} alt="" className="absolute inset-0 size-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent px-2 pb-1.5 pt-5">
                  <span className="block truncate font-sans text-[11px] font-medium text-paper">
                    {a.fileName}
                  </span>
                </span>
              </>
            ) : (
              <div className="flex h-full flex-col p-2.5">
                <span className="font-sans text-[12.5px] font-medium leading-snug text-ink [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] overflow-hidden">
                  {a.fileName}
                </span>
                {ext ? (
                  <span className="mt-auto inline-block w-fit rounded-[6px] bg-line-soft px-1.5 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide text-ink-soft">
                    {ext}
                  </span>
                ) : null}
              </div>
            )}

            {a.status === "uploading" ? (
              <span className="absolute inset-0 grid place-items-center bg-paper/65">
                <Spinner size={18} label={t("chat.attachments.uploading")} />
              </span>
            ) : null}

            {a.status === "error" ? (
              <span className="absolute inset-x-0 bottom-0 bg-ink/85 px-2 py-1 text-center font-sans text-[10px] font-medium text-paper">
                {t("chat.attachments.failed")}
              </span>
            ) : null}

            <button
              type="button"
              onClick={() => onRemove(a.localId)}
              aria-label={t("chat.attachments.remove")}
              className="absolute right-1.5 top-1.5 grid size-[22px] place-items-center rounded-full bg-paper/90 text-ink-soft opacity-0 shadow-[var(--shadow-soft)] outline-none transition-opacity duration-150 ease-house hover:text-ink focus-visible:opacity-100 group-hover:opacity-100 [&_svg]:size-3.5"
            >
              <CloseIcon />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
